/**
 * Status badge, stale hint, status filter chips and floor-plan legend (tri-server#1364).
 * Colours and cues come from `utilities/assetStatus`, so badge, chips, legend and plan markers
 * agree — and none of them says it with colour alone.
 */

import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Ban, Check, CircleAlert, Clock, Minus, TriangleAlert, Wrench, type LucideIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  ASSET_DISPLAY_STATUSES,
  NEUTRAL_MARKER_COLOR,
  DISPLAY_STATUS_BADGE_CLASSES,
  DISPLAY_STATUS_MARKER_COLORS,
  type AssetDisplayStatus,
  type StatusCounts,
  type StatusFilter,
} from "../utilities/assetStatus";

/** Shape cue per display status — the same meaning as the glyph on the plan marker. */
export const DISPLAY_STATUS_ICONS: Record<AssetDisplayStatus, LucideIcon> = {
  critical: CircleAlert,
  warning: TriangleAlert,
  offline: Clock,
  maintenance: Wrench,
  noData: Minus,
  normal: Check,
  decommissioned: Ban,
};

/** Translated status reason, or `undefined` for a missing or unknown key (never the raw key). */
export function useStatusReasonText() {
  const { t } = useTranslation();

  return (reason?: string | null) => {
    if (!reason) return undefined;
    const key = `assetQuickView.reason.${reason}`;
    const text = t(key);
    return text && text !== key ? text : undefined;
  };
}

export function AssetStatusBadge({
  status,
  stale,
  tooltip,
  className,
}: {
  status: AssetDisplayStatus;
  /** Data behind the status is old: a clock in the badge (the hint line says since when). */
  stale?: boolean;
  /** Tooltip content (reason, temperature, timestamp); none when empty. */
  tooltip?: ReactNode;
  className?: string;
}) {
  const { t } = useTranslation();

  const badge = (
    <Badge
      data-testid="asset-status-badge"
      data-status={status}
      data-stale={stale ? "true" : undefined}
      className={cn(
        "shrink-0 gap-1 whitespace-nowrap px-1.5 py-0 text-[11px] font-medium leading-5 shadow-none",
        DISPLAY_STATUS_BADGE_CLASSES[status],
        className,
      )}
    >
      {stale && status !== "offline" ? <Clock aria-hidden="true" className="h-3 w-3" /> : null}
      {t(`assetQuickView.status.${status}`)}
      {stale && status !== "offline" ? <span className="sr-only">{` (${t("assetQuickView.stale")})`}</span> : null}
    </Badge>
  );

  if (!tooltip) return badge;

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        {/* Badge is a plain function component (no ref), so the trigger wraps it. */}
        <TooltipTrigger asChild>
          <span tabIndex={0} className="inline-flex shrink-0 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            {badge}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs text-xs">
          {tooltip}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/** "Stand 24.09., 07:31 – veraltet": the values next to it are not current. Muted amber. */
export function StaleHint({ text, className }: { text: string; className?: string }) {
  return (
    <span
      data-testid="asset-stale-hint"
      className={cn("inline-flex min-w-0 items-center gap-1 text-[11px] leading-4 text-warning/90", className)}
    >
      <Clock aria-hidden="true" className="h-3 w-3 shrink-0" />
      <span className="truncate">{text}</span>
    </span>
  );
}

/** Small round swatch in the status colour with the status' shape cue. */
function StatusSwatch({ status, dashed }: { status: AssetDisplayStatus; dashed?: boolean }) {
  const Icon = DISPLAY_STATUS_ICONS[status];
  return (
    <span
      aria-hidden="true"
      className={cn(
        "inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full text-white",
        dashed && "border border-dashed border-current bg-transparent",
      )}
      style={
        dashed
          ? { color: DISPLAY_STATUS_MARKER_COLORS[status] }
          : { backgroundColor: DISPLAY_STATUS_MARKER_COLORS[status] }
      }
    >
      <Icon className="h-2.5 w-2.5" strokeWidth={3} />
    </span>
  );
}

/**
 * Status overview with filter: "Alle 20 · 2 Kritisch · 12 Offline · 5 Normal · 1 Wartung".
 * Only statuses that occur get a chip; a separate "veraltet" chip covers old data across
 * statuses. A click filters, a second click on the active chip returns to "Alle".
 */
export function AssetStatusFilterBar({
  counts,
  value,
  onChange,
  loading,
  error,
  onRetry,
  className,
}: {
  counts: StatusCounts;
  value: StatusFilter;
  onChange: (filter: StatusFilter) => void;
  loading?: boolean;
  error?: boolean;
  /** Reload the statuses after a failed request; the retry button only shows with it. */
  onRetry?: () => void;
  className?: string;
}) {
  const { t } = useTranslation();

  const chip = (filter: StatusFilter, count: number, label: string, swatch?: ReactNode) => {
    const active = value === filter;
    return (
      <button
        key={filter}
        type="button"
        data-testid={`status-filter-${filter}`}
        aria-pressed={active}
        onClick={() => onChange(active && filter !== "all" ? "all" : filter)}
        className={cn(
          "inline-flex h-7 shrink-0 items-center gap-1.5 rounded-full border px-2.5 text-xs transition-colors",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          active
            ? "border-foreground/70 bg-foreground text-background"
            : "border-border bg-background text-foreground hover:bg-muted",
        )}
      >
        {swatch}
        <span className="font-semibold tabular-nums">{count}</span>
        <span className={cn(active ? "text-background/80" : "text-muted-foreground")}>{label}</span>
      </button>
    );
  };

  return (
    <div
      data-testid="asset-status-filter"
      role="group"
      aria-label={t("assetQuickView.filter.label")}
      className={cn("flex min-w-0 flex-wrap items-center gap-1.5", className)}
    >
      {chip("all", counts.all, t("assetQuickView.filter.all"))}
      {ASSET_DISPLAY_STATUSES.filter((status) => counts[status] > 0).map((status) =>
        chip(status, counts[status], t(`assetQuickView.status.${status}`), <StatusSwatch status={status} />),
      )}
      {counts.stale > 0 ? (
        <>
          <span aria-hidden="true" className="mx-0.5 h-4 w-px bg-border" />
          {chip(
            "stale",
            counts.stale,
            t("assetQuickView.filter.stale"),
            <Clock aria-hidden="true" className="h-3.5 w-3.5 text-warning" />,
          )}
        </>
      ) : null}
      {loading ? (
        <span role="status" className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <span aria-hidden="true" className="inline-block h-2 w-2 animate-pulse rounded-full bg-muted-foreground/40" />
          {t("assetQuickView.legendLoading")}
        </span>
      ) : error ? (
        <span role="status" className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
          {t("assetQuickView.error")}
          {onRetry ? (
            <button
              type="button"
              data-testid="status-retry"
              onClick={onRetry}
              className="rounded text-[11px] font-medium text-foreground underline underline-offset-2 hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {t("assetQuickView.retry")}
            </button>
          ) : null}
        </span>
      ) : null}
    </div>
  );
}

/** Legend of the plan markers: colour + cue per status, dashed ring = old data. One line. */
export function AssetStatusLegend({
  loading,
  error,
  className,
}: {
  loading?: boolean;
  error?: boolean;
  className?: string;
}) {
  const { t } = useTranslation();

  return (
    <div
      data-testid="asset-status-legend"
      aria-label={t("assetQuickView.legend")}
      className={cn(
        "flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 text-[11px] leading-4 text-muted-foreground",
        className,
      )}
    >
      {ASSET_DISPLAY_STATUSES.map((status) => (
        <span key={status} className="inline-flex items-center gap-1">
          <StatusSwatch status={status} />
          {t(`assetQuickView.status.${status}`)}
        </span>
      ))}
      <span className="inline-flex items-center gap-1">
        <StatusSwatch status="normal" dashed />
        {t("assetQuickView.legendDashed")}
      </span>
      {loading || error ? (
        // Explains the neutral marker look: grey thin box, no cue dot, until the status is known.
        <span role="status" className="inline-flex items-center gap-1">
          <span
            aria-hidden="true"
            data-testid="legend-neutral-swatch"
            className={cn("inline-block h-3 w-3 shrink-0 rounded-[3px] border", loading && "animate-pulse")}
            style={{ borderColor: NEUTRAL_MARKER_COLOR }}
          />
          {loading ? t("assetQuickView.legendLoading") : t("assetQuickView.legendError")}
        </span>
      ) : null}
    </div>
  );
}
